import { ACTION_LABEL } from "@/lib/i18nLabels";
import { useEffect, useState, useMemo } from "react";
import { Plus, X, ListChecks, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import SearchableSelect from "@/components/SearchableSelect";
import api, { formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useProjectFilter } from "@/lib/projectFilter";
import { PRIORITY_LABEL } from "@/lib/constants";
import ChecklistAssigneePicker from "@/components/tasks/ChecklistAssigneePicker";
import TeamCCPicker from "@/components/tasks/TeamCCPicker";

// Divisions di-fetch dari API `/api/divisions` (source of truth di Settings → Divisions).
// Sebelumnya hardcoded `["BOD", "FAT", "Procurement", "Project"]` — kurang HRGA & nama Procurement
// salah (harusnya "Procurement Asset Logistic"). Bug: dropdown tidak sinkron dengan divisi aktual.
const PRIORITIES = ["Rendah", "Sedang", "Tinggi", "Kritis"];

export default function CreateTaskDialog({ open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const { projects, selectedId, headOffice } = useProjectFilter();
  const [users, setUsers] = useState([]);
  const [divisions, setDivisions] = useState([]); // ← fetched dari /api/divisions (Settings → Divisions)
  const [form, setForm] = useState({
    project_id: "", title: "", description: "",
    division: "", sub_division: "", assignee_id: "",
    priority: "Sedang", deadline: "", wo_phase: "",
    cc_user_ids: [],
  });
  const [busy, setBusy] = useState(false);
  // 149-cw-r134: Checklist items untuk task (subtasks).
  const [checklist, setChecklist] = useState([]); // [{tmpId, text}]
  const [checklistInput, setChecklistInput] = useState("");

  useEffect(() => {
    if (open) {
      // Ambil users + divisions paralel supaya dropdown Division selalu match dengan
      // master data di Settings (auto-sync — tambah/hapus/ubah di Settings langsung
      // ke-reflect di sini setiap dialog dibuka).
      api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
      api.get("/divisions").then(({ data }) => setDivisions((data || []).map((d) => d.name))).catch(() => {});
      const defaultProject = selectedId || headOffice?.id || "";
      setForm({
        project_id: defaultProject, title: "", description: "",
        division: "", sub_division: "", assignee_id: "",
        priority: "Sedang", deadline: "", wo_phase: "",
        cc_user_ids: [],
      });
      setChecklist([]);
      setChecklistInput("");
    }
  }, [open, selectedId, headOffice?.id]);

  const addChecklistItem = () => {
    const t = checklistInput.trim();
    if (!t) return;
    // Support paste multi-line: split by newline, add each non-empty.
    // 149-cw-r135: default per-item assignee = form.assignee_id (task-level PIC).
    // User dapat override lewat picker inline nantinya.
    const lines = t.split("\n").map((l) => l.trim()).filter(Boolean);
    setChecklist((prev) => [
      ...prev,
      ...lines.map((line) => ({
        tmpId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        text: line.slice(0, 500),
        assignee_id: form.assignee_id || null,
      })),
    ]);
    setChecklistInput("");
  };
  const removeChecklistItem = (tmpId) => {
    setChecklist((prev) => prev.filter((it) => it.tmpId !== tmpId));
  };
  const setItemAssignee = (tmpId, uid) => {
    setChecklist((prev) => prev.map((it) => it.tmpId === tmpId ? { ...it, assignee_id: uid } : it));
  };

  // 2026-06-22 (user request): pengisian FLEKSIBEL — tidak harus mulai dari
  // Division. Bisa langsung pilih Assignee (Division & Sub Division otomatis
  // terisi dari data user) atau mulai dari Sub Division (Division otomatis
  // terisi). Ketiga kolom saling mengisi; pilihan yang sudah diisi hanya
  // MEMFILTER opsi kolom lain.
  const allSubDivisions = useMemo(
    () => Array.from(new Set(users.map((u) => u.sub_division).filter(Boolean))).sort(),
    [users],
  );
  const subToDivision = useMemo(() => {
    const m = new Map();
    for (const u of users) {
      if (u.sub_division && u.division && !m.has(u.sub_division)) m.set(u.sub_division, u.division);
    }
    return m;
  }, [users]);

  const subDivisions = useMemo(() => {
    // Tanpa division → tampilkan SEMUA sub-divisi (user bisa mulai dari sini).
    if (!form.division) return allSubDivisions;
    // Filter user yang tidak punya sub_division (null/empty) supaya Radix Select tidak crash
    // dengan error "SelectItem must have a value prop that is not an empty string".
    return Array.from(new Set(
      users
        .filter((u) => u.division === form.division && u.sub_division)
        .map((u) => u.sub_division)
    )).filter(Boolean);
  }, [form.division, users, allSubDivisions]);

  const eligibleUsers = useMemo(() => {
    // `user?.id` optional chain — kalau context user belum siap (auth still loading), jangan crash.
    return users.filter((u) => {
      if (!u || u.id === user?.id) return false;
      if (form.division && u.division !== form.division) return false;
      if (form.sub_division && u.sub_division !== form.sub_division) return false;
      return true;
    });
  }, [form.division, form.sub_division, users, user]);

  // Assignee picker: searchable, digrup per divisi; label menyertakan sub-divisi.
  const assigneeGroups = useMemo(() => {
    const byDiv = new Map();
    for (const u of eligibleUsers) {
      if (!u.id || !String(u.id).trim()) continue;
      const d = u.division || "Tanpa Divisi";
      if (!byDiv.has(d)) byDiv.set(d, []);
      byDiv.get(d).push({ value: u.id, label: `${u.name}${u.sub_division ? ` · ${u.sub_division}` : ""}` });
    }
    return [...byDiv.entries()].map(([label, options]) => ({ label, options }));
  }, [eligibleUsers]);

  // Handler saling-mengisi antar kolom Division / Sub Division / Assignee.
  const onDivisionChange = (v) => setForm((f) => {
    const next = { ...f, division: v };
    if (f.sub_division) {
      const stillValid = users.some((u) => u.division === v && u.sub_division === f.sub_division);
      if (!stillValid) { next.sub_division = ""; next.assignee_id = ""; }
    }
    if (next.assignee_id) {
      const au = users.find((u) => u.id === next.assignee_id);
      if (au && au.division !== v) next.assignee_id = "";
    }
    return next;
  });
  const onSubDivisionChange = (v) => setForm((f) => {
    // Auto-isi Division dari sub-divisi yang dipilih (fallback: pertahankan).
    const div = subToDivision.get(v) || f.division || "";
    const next = { ...f, sub_division: v, division: div };
    if (next.assignee_id) {
      const au = users.find((u) => u.id === next.assignee_id);
      if (au && au.sub_division !== v) next.assignee_id = "";
    }
    return next;
  });
  const onAssigneeChange = (v) => setForm((f) => {
    if (!v) return { ...f, assignee_id: "" };
    const au = users.find((u) => u.id === v);
    // Auto-isi Division & Sub Division dari data user terpilih.
    return {
      ...f,
      assignee_id: v,
      division: au ? (au.division || "") : f.division,
      sub_division: au ? (au.sub_division || "") : f.sub_division,
    };
  });

  // Defensive filter — Radix Select CRASH kalau SelectItem punya value kosong/null.
  // Root cause bug 149-k (pattern sama dgn LegacyUsersBanner untuk sub_division).
  // Kalau data legacy punya id kosong atau race fetch, skip alih-alih render.
  const safeProjects = useMemo(
    () => (projects || []).filter((p) => p && typeof p.id === "string" && p.id.trim().length > 0),
    [projects],
  );
  const internalProjects = safeProjects.filter((p) => p.project_type === "internal");
  const newProjects = safeProjects.filter((p) => p.project_type === "new_project");
  const voProjects = safeProjects.filter((p) => p.project_type === "variation_order");
  const woProjects = safeProjects.filter((p) => p.project_type === "working_order");

  // WOs for the currently-picked parent project (so user can drill down after clicking a project)
  const selectedProject = safeProjects.find((p) => p.id === form.project_id) || null;
  const parentIdForWO = selectedProject
    ? (selectedProject.project_type === "new_project" ? selectedProject.id : selectedProject.parent_project_id)
    : null;
  const relatedWOs = parentIdForWO
    ? woProjects.filter((w) => w.parent_project_id === parentIdForWO)
    : [];
  const activeWOId = selectedProject?.project_type === "working_order" ? selectedProject.id : "";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.project_id) { toast.error("Please select a project"); return; }
    if (!form.assignee_id) { toast.error("Please select an assignee"); return; }
    setBusy(true);
    try {
      await api.post("/tasks", {
        project_id: form.project_id,
        title: form.title,
        description: form.description,
        assignee_id: form.assignee_id,
        priority: form.priority,
        deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
        wo_phase: form.wo_phase || null,
        checklist: checklist.map((it) => ({ text: it.text, assignee_id: it.assignee_id || null })),
        cc_user_ids: form.cc_user_ids,
      });
      toast.success("Task created");
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-2xl" data-testid="dialog-create-task">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">New Task</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="overline text-slate-600">Project <span className="text-rose-600 normal-case">*</span></Label>
            {/* 149-cw-r178: project picker searchable — ketik nama utk filter. */}
            <SearchableSelect
              testid="select-project"
              value={form.project_id}
              onChange={(v) => {
                const p = projects.find((x) => x.id === v);
                setForm({ ...form, project_id: v, wo_phase: p?.project_type === "working_order" ? form.wo_phase : "" });
              }}
              placeholder="Select project…"
              searchPlaceholder="Ketik nama project / client / WO…"
              groups={[
                ...(internalProjects.length > 0 ? [{ label: "Internal (default)", options: internalProjects.map((p) => ({ value: p.id, label: p.project_name })) }] : []),
                ...(newProjects.length > 0 ? [{ label: "Projects", options: newProjects.map((p) => ({ value: p.id, label: `${p.project_name}${p.client ? ` · ${p.client}` : ""}` })) }] : []),
                ...(voProjects.length > 0 ? [{ label: "Variation Orders", options: voProjects.map((p) => ({ value: p.id, label: `${p.parent_project_name} · VO: ${p.variation_order_name}` })) }] : []),
                ...(woProjects.length > 0 ? [{ label: "Working Orders", options: woProjects.map((p) => ({ value: p.id, label: `${p.parent_project_name} · WO: ${p.wo_number || "—"}` })) }] : []),
              ]}
            />
            <p className="text-[10px] text-slate-500">Head Office is the default if nothing is picked.</p>
          </div>

          {relatedWOs.length > 0 && (
            <div className="space-y-1.5" data-testid="wo-picker-wrap">
              <Label className="overline text-slate-600">Working Order</Label>
              {/* 149-cw-r178: WO picker searchable. */}
              <SearchableSelect
                testid="select-working-order"
                value={activeWOId || "__none"}
                onChange={(v) => setForm({
                  ...form,
                  project_id: v === "__none" ? parentIdForWO : v,
                  wo_phase: v === "__none" ? "" : form.wo_phase,
                })}
                placeholder="Pilih working order…"
                searchPlaceholder="Ketik nomor WO / PO…"
                options={[
                  { value: "__none", label: "— Tidak pakai WO (project utama) —" },
                  ...relatedWOs.map((w) => ({ value: w.id, label: `WO: ${w.wo_number || "—"}${w.po_number ? ` · PO: ${w.po_number}` : ""}` })),
                ]}
              />
              <p className="text-[10px] text-slate-500">Project ini punya {relatedWOs.length} Working Order. Klik untuk memilih.</p>
            </div>
          )}

          {activeWOId && (
            <div className="space-y-1.5" data-testid="wo-phase-picker">
              <Label className="overline text-slate-600">Tahap Working Order</Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { k: "survey_sph", l: "Survey & SPH" },
                  { k: "project", l: "Project" },
                  { k: "invoice", l: "Invoice" },
                ].map((ph) => {
                  const active = form.wo_phase === ph.k;
                  return (
                    <button
                      key={ph.k}
                      type="button"
                      data-testid={`wo-phase-${ph.k}`}
                      onClick={() => setForm({ ...form, wo_phase: active ? "" : ph.k })}
                      className={`px-3 py-1.5 text-xs uppercase tracking-wider font-semibold border transition-colors ${
                        active
                          ? "bg-[#0B1D3A] border-[#0B1D3A] text-white"
                          : "bg-white border-slate-300 text-slate-700 hover:border-[#0B1D3A]"
                      }`}
                    >
                      {ph.l}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-500">Pilih tahap task ini pada Working Order. Klik lagi untuk hapus.</p>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="overline text-slate-600">Division</Label>
              <SearchableSelect
                testid="select-division"
                value={form.division}
                onChange={onDivisionChange}
                options={divisions.filter((d) => typeof d === "string" && d.trim().length > 0).map((d) => ({ value: d, label: d }))}
                placeholder="Select…"
                searchPlaceholder="Cari divisi…"
                emptyText="Divisi tidak ditemukan"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="overline text-slate-600">Sub Division</Label>
              {/* 2026-06-22: tidak lagi disabled menunggu Division — memilih sub
                  otomatis mengisi Division. Tanpa Division → tampilkan semua sub. */}
              <SearchableSelect
                testid="select-subdivision"
                value={form.sub_division}
                onChange={onSubDivisionChange}
                options={subDivisions.map((s) => ({ value: s, label: s }))}
                placeholder="Select…"
                searchPlaceholder="Cari sub divisi…"
                emptyText="Sub divisi tidak ditemukan"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="overline text-slate-600">Assignee <span className="text-rose-600 normal-case">*</span></Label>
              {/* 2026-06-22 (user request): bisa LANGSUNG pilih assignee tanpa
                  mengisi Division/Sub Division dulu — keduanya otomatis terisi
                  dari data user terpilih. Searchable karena daftar bisa panjang. */}
              <SearchableSelect
                testid="select-assignee"
                value={form.assignee_id}
                onChange={onAssigneeChange}
                placeholder="Ketik nama personel…"
                searchPlaceholder="Cari nama / divisi / sub-divisi…"
                emptyText="Tidak ada personel yang cocok"
                groups={assigneeGroups}
              />
            </div>
          </div>
          <p className="text-[10px] text-slate-500 -mt-2" data-testid="assignee-flex-hint">
            Bisa mulai dari mana saja — pilih Assignee langsung (Division &amp; Sub Division terisi otomatis), atau pilih Sub Division dulu (Division terisi otomatis).
          </p>

          {/* Team CC (2026-06-20): follower task — otomatis menerima notif setiap update/perubahan status. */}
          <div className="space-y-1.5" data-testid="team-cc-section">
            <Label className="overline text-slate-600 flex items-center gap-1.5">
              <Users size={12} /> Team CC <span className="text-slate-400 normal-case">(opsional — mengikuti & menerima notifikasi update task)</span>
            </Label>
            <TeamCCPicker users={users} value={form.cc_user_ids} excludeIds={[form.assignee_id, user?.id].filter(Boolean)}
              onChange={(ids) => setForm({ ...form, cc_user_ids: ids })} testid="team-cc" />
          </div>

          <div className="space-y-1.5">
            <Label className="overline text-slate-600">Task Title</Label>
            <Input data-testid="task-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none" placeholder="e.g. Review vendor contract" />
          </div>
          <div className="space-y-1.5">
            <Label className="overline text-slate-600">Description</Label>
            <Textarea data-testid="task-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none resize-none" />
          </div>

          {/* 149-cw-r134: Checklist / subtasks input. */}
          <div className="space-y-1.5" data-testid="checklist-section">
            <Label className="overline text-slate-600 flex items-center gap-1.5">
              <ListChecks size={12} /> Checklist / Sub-tugas <span className="text-slate-400 normal-case">(opsional)</span>
            </Label>
            {checklist.length > 0 && (
              <div className="border border-slate-200 divide-y divide-slate-100" data-testid="checklist-list">
                {checklist.map((it, idx) => (
                  <div key={it.tmpId} className="flex items-center gap-2 px-2 py-1.5 bg-slate-50" data-testid={`checklist-item-${idx}`}>
                    <span className="w-4 h-4 border border-slate-400 shrink-0" aria-hidden="true" />
                    <span className="text-xs text-slate-700 flex-1 min-w-0 truncate" title={it.text}>{it.text}</span>
                    <ChecklistAssigneePicker
                      value={it.assignee_id}
                      users={users}
                      onChange={(uid) => setItemAssignee(it.tmpId, uid)}
                      compact
                    />
                    <button
                      type="button"
                      onClick={() => removeChecklistItem(it.tmpId)}
                      data-testid={`checklist-remove-${idx}`}
                      className="text-slate-400 hover:text-rose-600 p-1"
                      aria-label="Hapus item"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Input
                value={checklistInput}
                onChange={(e) => setChecklistInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    addChecklistItem();
                  }
                }}
                placeholder="Ketik lalu tekan Enter untuk menambah item…"
                data-testid="checklist-input"
                className="rounded-none flex-1"
              />
              <Button
                type="button"
                onClick={addChecklistItem}
                data-testid="checklist-add-btn"
                disabled={!checklistInput.trim()}
                className="rounded-none bg-[#0B1D3A] hover:bg-[#122a4d] text-white h-9 px-3 uppercase text-xs tracking-wider font-semibold disabled:opacity-50"
              >
                <Plus size={13} className="mr-1" /> Tambah
              </Button>
            </div>
            {checklist.length > 0 && (
              <p className="text-[10px] text-slate-500">{checklist.length} item · Anda bisa centang setelah task dibuat.</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="overline text-slate-600">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="select-priority" className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABEL[p]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="overline text-slate-600">Deadline</Label>
              <Input data-testid="task-deadline" type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="rounded-none" />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>{ACTION_LABEL.cancel}</Button>
            <Button type="submit" data-testid="task-submit" disabled={busy} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]">
              {busy ? "Saving…" : "Create Task"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
